import { Link } from 'react-router-dom';
import { QrImage } from '../../components/QrImage';
import { useSession } from '../../lib/useSession';
import { Chip } from '../../components/Chip';
import { LIST_MAX_HEIGHT, Table, type Column } from '../../components/Table';
import { formatNumber, stateChip, typeLabel } from './states';
import type { QrSummary } from './types';
import { partnerQrImagePath, qrHref } from './utils';

export type QrColumn = 'thumb' | 'code' | 'type' | 'lot' | 'business' | 'ref' | 'target' | 'state' | 'custodian' | 'scans' | 'activatedBy' | 'actions';


function shortTarget(url?: string | null): string {
  if (!url) return '—';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function QrThumb({ qr }: { qr: QrSummary }) {
  const { activeOrg } = useSession();
  if (qr.artStatus === 'generando') {
    return <div className="h-11 w-11 animate-pulse rounded-md bg-green-soft motion-reduce:animate-none" role="status" aria-label={`Generando QR Art ${qr.code}`} />;
  }
  return <QrImage lazy className="h-11 w-11" path={partnerQrImagePath(qr, { size: 256 })} orgId={activeOrg?.orgId} alt={`QR ${qr.code}`} />;
}

function columns(names: Record<string, string>): Record<QrColumn, Column<QrSummary>> {
  return {
  thumb: { key: 'thumb', header: 'QR', render: (r) => <QrThumb qr={r} /> },
  code: {
    key: 'code',
    header: 'Código',
    mono: true,
    render: (r) => (
      <span className="font-semibold tracking-wider">
        {r.code}
        {r.domain && r.domain !== 'q-r.cl' && <span className="ml-1 text-xs font-normal text-ink-muted">{r.domain}</span>}
      </span>
    ),
  },
  type: { key: 'type', header: 'Tipo', render: (r) => (typeLabel(r) === 'QR Art' ? <Chip tone="art" dot={false}>QR Art</Chip> : typeLabel(r)) },
  lot: { key: 'lot', header: 'Lote', mono: true, render: (r) => r.lotId ?? '—' },
  business: { key: 'business', header: 'Negocio', render: (r) => r.business?.name ?? '—' },
  ref: { key: 'ref', header: 'Referencia externa', mono: true, render: (r) => r.business?.externalRef ?? '—' },
  target: { key: 'target', header: 'Destino', render: (r) => <span className="block max-w-[16rem] truncate" title={r.targetUrl ?? undefined}>{shortTarget(r.targetUrl)}</span> },
  state: {
    key: 'state',
    header: 'Estado',
    render: (r) => {
      const chip = stateChip(r);
      return <Chip tone={chip.tone}>{chip.label}</Chip>;
    },
  },
  custodian: { key: 'custodian', header: 'Custodio', render: (r) => r.custodianName ?? (r.custodianUid ? (names[r.custodianUid] ?? r.custodianUid) : '—') },
  scans: { key: 'scans', header: 'Escaneos 30 d', align: 'right', render: (r) => <span className="tabular-nums">{formatNumber(r.scans30d ?? r.hits)}</span> },
  activatedBy: { key: 'activatedBy', header: 'Activó', render: (r) => r.activatedByName ?? (r.activatedBy ? (names[r.activatedBy] ?? r.activatedBy) : '—') },
  actions: {
    key: 'actions',
    header: '',
    align: 'right',
    render: (r) => (
      <Link to={qrHref(r)} className="font-medium text-green underline hover:text-ink" aria-label={`Ver ficha de ${r.code}`}>
        Ver
      </Link>
    ),
  },
  };
}

export interface QrTableProps {
  rows: QrSummary[];
  columns: QrColumn[];
  caption: string;
  loading?: boolean;
  empty?: React.ReactNode;
  /** uid → nombre visible para custodio y quien activó. */
  names?: Record<string, string>;
}

export function QrTable({ rows, columns: cols, loading, empty, caption, names = {} }: QrTableProps) {
  const all = columns(names);
  return <Table caption={caption} columns={cols.map((c) => all[c])} rows={rows} rowKey={(r) => r.id ?? `${r.domain}:${r.code}`} loading={loading} empty={empty} stickyHeader maxHeight={LIST_MAX_HEIGHT} />;
}
